import { Badge, Box, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { DataView } from '@components/DataView/DataView'
import {
  admissionSectionField,
  BirthingCenterAdmissionData,
  Encounter,
  FieldMapValue,
  getFullName,
  objectToArray,
} from '@hb/shared'
import { useMemo } from 'react'
import { usePracticeProviders } from '../../../../components/Practices/hooks/usePracticeProviders'

// Static-only view field — provider fields (admittingMidwife, supportPersons,
// additionalProvidersPresent) are excluded because they have empty options in the shared
// FieldMap; they're rendered manually below.
// supervisoryConsultation is also excluded — we render it manually to show the timestamp.
const {
  admittingMidwife: _am,
  supportPersons: _sp,
  additionalProvidersPresent: _app,
  supervisoryConsultation: _sc,
  ...staticChildren
} = admissionSectionField.children
const staticAdmissionField = { ...admissionSectionField, children: staticChildren }

const LabelText = ({ children }: { children: React.ReactNode }) => (
  <Text
    fontSize="xs"
    color="gray.500"
    fontWeight={600}
    textTransform="uppercase"
    letterSpacing="0.04em">
    {children}
  </Text>
)

export const BirthingCenterAdmissionView = ({
  data,
  encounter,
}: {
  data: FieldMapValue
  encounter: Encounter
}) => {
  const admissionData = data as BirthingCenterAdmissionData | undefined
  const { data: providers } = usePracticeProviders(encounter.practiceId)
  const providersArr = useMemo(() => objectToArray(providers ?? {}), [providers])

  const resolveProviderName = (id: string): string => {
    const p = providersArr.find(pr => pr.id === id)
    return p ? getFullName(p) : id
  }

  const admittingMidwifeName = useMemo(() => {
    if (!admissionData?.admittingMidwife) return '—'
    return resolveProviderName(admissionData.admittingMidwife)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionData, providersArr])

  const supportPersonNames = useMemo(() => {
    if (!admissionData?.supportPersons?.length) return null
    return admissionData.supportPersons.map(id => resolveProviderName(id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionData, providersArr])

  const additionalProviderNames = useMemo(() => {
    if (!admissionData?.additionalProvidersPresent?.length) return null
    return admissionData.additionalProvidersPresent.map(id => resolveProviderName(id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionData, providersArr])

  const supervisoryConsultation = admissionData?.supervisoryConsultation
  const providerHandoffs = admissionData?.providerHandoffs

  return (
    <Box w="100%">
      <DataView
        noHeader
        alwaysExpanded
        field={staticAdmissionField}
        data={data ?? {}}
        boxProps={{ border: 'none', borderBottom: 'none' }}
      />
      <Flex direction="column" gap={2} px={1} mt={1}>
        {/* Admitting Midwife */}
        <Flex direction="column" gap={0}>
          <LabelText>Admitting Midwife</LabelText>
          <Text fontSize="sm" fontWeight={500}>
            {admittingMidwifeName}
          </Text>
        </Flex>

        {/* §3.1: Referring provider — shown only for Transfer In admission type */}
        {admissionData?.admissionType === 'transfer-in' && admissionData?.referringProvider && (
          <Flex direction="column" gap={0}>
            <LabelText>Referring Provider</LabelText>
            <Text fontSize="sm" fontWeight={500}>
              {admissionData.referringProvider}
            </Text>
          </Flex>
        )}

        {/* Support Persons */}
        {supportPersonNames && (
          <Flex direction="column" gap={0}>
            <LabelText>Support Persons</LabelText>
            {supportPersonNames.map((name, i) => (
              <Text key={i} fontSize="sm" fontWeight={500}>
                {name}
              </Text>
            ))}
          </Flex>
        )}

        {/* Additional Providers Present (P2-A) */}
        {additionalProviderNames && (
          <Flex direction="column" gap={0}>
            <LabelText>Additional Providers Present</LabelText>
            {additionalProviderNames.map((name, i) => (
              <Text key={i} fontSize="sm" fontWeight={500}>
                {name}
              </Text>
            ))}
          </Flex>
        )}

        {/* Supervisory Consultation (P2-B) */}
        {supervisoryConsultation?.note && (
          <Flex direction="column" gap={0}>
            <LabelText>Supervisory Consultation</LabelText>
            <Text fontSize="sm" fontWeight={500}>
              {supervisoryConsultation.note}
            </Text>
            {supervisoryConsultation.recordedAt ? (
              <Text fontSize="xs" color="gray.400">
                Recorded {new Date(supervisoryConsultation.recordedAt).toLocaleString()}
              </Text>
            ) : null}
          </Flex>
        )}

        {/* Provider Handoffs (P2-C) */}
        {providerHandoffs && providerHandoffs.length > 0 && (
          <Flex direction="column" gap={2}>
            <LabelText>Provider Handoffs</LabelText>
            {providerHandoffs.map(handoff => (
              <Box
                key={handoff.id}
                borderRadius={6}
                border="1px solid"
                borderColor="gray.200"
                bg="gray.50"
                p={3}>
                <HStack justify="space-between" align="flex-start">
                  <VStack align="stretch" spacing={0}>
                    <Text fontSize="xs" color="gray.500">
                      {new Date(handoff.handoffAt).toLocaleString()}
                    </Text>
                    <Text fontSize="sm">
                      <Text as="span" fontWeight={600}>
                        Out:
                      </Text>{' '}
                      {resolveProviderName(handoff.outgoingProviderId)}
                    </Text>
                    <Text fontSize="sm">
                      <Text as="span" fontWeight={600}>
                        In:
                      </Text>{' '}
                      {resolveProviderName(handoff.incomingProviderId)}
                    </Text>
                    {handoff.handoffNote && (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        {handoff.handoffNote}
                      </Text>
                    )}
                  </VStack>
                  <Box>
                    {handoff.acknowledgedAt ? (
                      <VStack align="flex-end" spacing={0}>
                        <Badge colorScheme="green" fontSize="xs">
                          Acknowledged
                        </Badge>
                        <Text fontSize="xs" color="gray.400">
                          {new Date(handoff.acknowledgedAt).toLocaleString()}
                        </Text>
                      </VStack>
                    ) : (
                      <Badge colorScheme="gray" fontSize="xs">
                        Pending acknowledgment
                      </Badge>
                    )}
                  </Box>
                </HStack>
              </Box>
            ))}
          </Flex>
        )}
      </Flex>
    </Box>
  )
}
